import React, { useRef, useState } from 'react';
import { Platform, Pressable, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

export const TOOLS_HERO_PAD_X = { xs: 15, sm: 20, lg: 20 };

function ToolsHero({ headline, subtitle, search, favorites, onBack }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const inputRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const padX = isXS ? TOOLS_HERO_PAD_X.xs : TOOLS_HERO_PAD_X.lg;
  const hasSearch = searchOpen || !!search?.value;
  const actionStyle = ({ hovered, pressed }) => ({
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7,
    minHeight: 40, paddingHorizontal: 10, borderRadius: 10,
    backgroundColor: hovered || pressed ? palette.hover : 'transparent',
  });

  const closeSearch = () => { search?.onChangeText(''); setSearchOpen(false); };

  return (
    <View style={{ width: '100%', paddingHorizontal: padX, paddingTop: isXS ? 20 : 28, paddingBottom: 12, gap: 8 }}>
      {onBack && <Pressable accessibilityRole="button" accessibilityLabel={t('pages.tools.allTools')}
        onPress={onBack} style={state => [actionStyle(state), { alignSelf: 'flex-start', marginLeft: -10, marginBottom: 6 }]}>
        <Icon name="arrow-left" size={12} color={palette.icon} />
        <Text size="s" color="secondary">{t('pages.tools.allTools')}</Text>
      </Pressable>}
      <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Text tag="h1" weight="bold" style={{ flex: 1, minWidth: 100, fontSize: 32, lineHeight: 38, letterSpacing: -0.8 }}>{headline}</Text>
        {favorites && <Pressable onPress={favorites.onPress} accessibilityRole="button"
          accessibilityLabel={t('pages.tools.myFavorites')} accessibilityState={{ selected: favorites.active }} style={actionStyle}>
          <Icon name="heart" solid={favorites.active} size={15} color={favorites.active ? colors.text_color_primary : palette.icon} />
          {!isXS && <Text size="s" color="secondary">{t('pages.tools.saved')}</Text>}
        </Pressable>}
        {search && <Pressable accessibilityRole="button" accessibilityLabel={t('pages.tools.searchPlaceholder')}
          accessibilityState={{ expanded: hasSearch }} onPress={() => { setSearchOpen(true); inputRef.current?.focus(); }} style={actionStyle}>
          <Icon name="magnifying-glass" size={16} color={palette.icon} />
          {!isXS && <Text size="s" color="secondary">{t('pages.tools.searchAction')}</Text>}
        </Pressable>}
      </View>
      {!!subtitle && <Text tag="p" color="secondary" style={{ fontSize: 14, lineHeight: 21, maxWidth: 640 }}>{subtitle}</Text>}
      {hasSearch && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12, minHeight: 44,
        paddingHorizontal: 12, borderRadius: 12, borderWidth: 1,
        borderColor: focused ? palette.activeBorder : palette.border, backgroundColor: palette.surface }}>
        <Icon name="magnifying-glass" size={16} color={palette.icon} />
        <TextInput ref={inputRef} autoFocus value={search.value ?? ''} onChangeText={search.onChangeText}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          onKeyPress={event => { if (event.nativeEvent.key === 'Escape') closeSearch(); }}
          placeholder={search.placeholder} accessibilityLabel={search.placeholder} placeholderTextColor={colors.text_color_tertiary}
          style={{ flex: 1, minWidth: 0, paddingVertical: 10, fontSize: 14, color: colors.text_color_primary, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) }}
          autoCorrect={false} autoCapitalize="none" returnKeyType="search" testID={search.testID} />
        <Pressable onPress={closeSearch} accessibilityRole="button" accessibilityLabel={t('pages.tools.closeSearch')} style={actionStyle}>
          <Icon name="xmark" size={14} color={palette.icon} />
        </Pressable>
      </View>}
    </View>
  );
}

export default React.memo(ToolsHero);
